import { Button, Paper } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import { ScgpUserQuery } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { UserDetailMode } from "../constants";

interface propsType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    wrapper: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      height: 88,
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px",
      padding: "0 3.2rem",
      "& .MuiButton-label": {
        fontWeight: "bold",
        "& img": {
          marginRight: "8px"
        }
      }
    },
    rightContainer: {
      display: "flex",
      alignItems: "center",
      marginLeft: "auto",
      "& .MuiButton-outlined": {
        marginRight: "1.6rem"
      }
    },
    leftContainer: {
      display: "flex",
      "& button": {
        marginRight: "1.6rem"
      }
    },
    textInfo: {
      fontSize: 12,
      color: theme.palette.info.main,
      marginRight: "0.5rem"
    },
    textValue: {
      fontSize: 12,
      color: theme.palette.text.primary,
      marginRight: "1rem"
    },
    menu: {
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props =>
          props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`
      }
    },
    primaryButton: {
      color: "#ffffff",
      fontWeight: 700,
      fontSize: "1.8rem",
      lineHeight: "28px",
      height: 48,
      padding: "0 33px"
    }
  }),
  { name: "UserDetailFooter" }
);

export interface UserDetailFooterProps {
  user: ScgpUserQuery;
  mode: UserDetailMode;
  setMode: (mode: UserDetailMode) => void;
  onSubmit: () => void;
}

const UserDetailFooter: React.FC<UserDetailFooterProps> = ({
  user,
  mode,
  setMode,
  onSubmit
}) => {
  const [appState, _] = useAppState();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const renderDatetime = datetime => {
    if (!datetime) {
      return "-";
    }
    return moment(datetime).format("DD/MM/YYYY HH:mm");
  };

  const renderName = (firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return "-";
  };

  return (
    <div className={classes.menu}>
      {mode === UserDetailMode.VIEW && (
        <Paper className={classes.wrapper}>
          <div
            className={classes.leftContainer}
            data-testid="userDetailContainer"
          >
            <MyTypography variant="body3" className={classes.textInfo}>
              {intl.formatMessage({
                defaultMessage: "เข้าใช้ล่าสุด:"
              })}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textValue}>
              {renderDatetime(user?.scgpUser?.lastLogin)}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textInfo}>
              {intl.formatMessage({
                defaultMessage: "วันที่สร้าง:"
              })}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textValue}>
              {renderDatetime(user?.scgpUser?.dateJoined)}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textInfo}>
              {intl.formatMessage({
                defaultMessage: "สร้างโดย:"
              })}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textValue}>
              {renderName(
                user?.scgpUser?.extendData?.createdBy?.firstName,
                user?.scgpUser?.extendData?.createdBy?.lastName
              )}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textInfo}>
              {intl.formatMessage({
                defaultMessage: "วันที่อัปเดต:"
              })}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textValue}>
              {renderDatetime(user?.scgpUser?.extendData?.updatedAt)}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textInfo}>
              {intl.formatMessage({
                defaultMessage: "อัปเดตโดย:"
              })}
            </MyTypography>
            <MyTypography variant="body3" className={classes.textValue}>
              {renderName(
                user?.scgpUser?.extendData?.updatedBy?.firstName,
                user?.scgpUser?.extendData?.updatedBy?.lastName
              )}
            </MyTypography>
          </div>
          <div className={classes.rightContainer}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setMode(UserDetailMode.EDIT)}
              className={classes.primaryButton}
              name="editButton"
              id="editButton"
              data-testid="editButton"
            >
              <FormattedMessage defaultMessage="แก้ไขข้อมูลผู้ใช้งาน" />
            </Button>
          </div>
        </Paper>
      )}
      {mode === UserDetailMode.EDIT && (
        <Paper className={classes.wrapper}>
          <div className={classes.rightContainer}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSubmit}
              className={classes.primaryButton}
              name="saveButton"
              id="saveButton"
              data-testid="saveButton"
            >
              <FormattedMessage defaultMessage="บันทึกข้อมูลผู้ใช้งาน" />
            </Button>
          </div>
        </Paper>
      )}
      {mode === UserDetailMode.CREATE && (
        <Paper className={classes.wrapper}>
          <div className={classes.rightContainer}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSubmit}
              className={classes.primaryButton}
              name="addUserButton"
              id="addUserButton"
              data-testid="addUserButton"
            >
              <FormattedMessage defaultMessage="เพิ่มผู้ใช้งาน" />
            </Button>
          </div>
        </Paper>
      )}
    </div>
  );
};

export default UserDetailFooter;
